import InnerTabs from 'components/Tabs/InnerTabs'
import useFlag from 'hooks/useFlag'
import useIsAdmin from 'hooks/useIsAdmin'
import { useOrderingAccess } from 'hooks/useOrderingAccess'
import { buildTranslate } from 'locales'
import React from 'react'
import { useSelector } from 'react-redux'
import ErrorMessages from 'scenes/OrderingManagement/ErrorMessages'
import { selectCurrentMerchant } from 'selectors/merchant'
import { useAbility } from 'utilities/ability'
import Analytics from './Analytics'
import CheckoutMessage from './CheckoutMessage'
import DeliveryDefaultLogic from './DeliveryDefaultLogic'
import InstoreEnrollment from './InstoreEnrollment'
import InstorePurchaseTracking from './InstorePurchaseTracking'
import OrderingConfiguration from './OrderingConfiguration'
import PostOrderFeedback from './PostOrderFeedback'
import { isPostOrderFeedbackHidden } from './PostOrderFeedback/constants'
import PreferencesModalSetting from './PreferencesModalSetting'
import PromoCodeVisibility from './PromoCodeVisibility'
import ReceiptSettings from './ReceiptSettings'
import ScannableCodes from './ScannableCodes'
import TermsAndPolicies from './TermsAndPolicies'
import TimeBasedMenuVisibility from './TimeBasedMenuVisibility'
import UnavailableItemVisibility from './UnavailableItemVisibility'

export enum Tabs {
  OrderingConfiguration = 'ordering_configuration',
  DeliveryDefaultLogic = 'delivery_default_logic',
  PreferencesModalSetting = 'preferences_modal_setting',
  ReceiptSettings = 'receipt_settings',
  ScannableCodes = 'scannable_codes',
  PostOrderFeedback = 'post_order_feedback',
  InstorePurchaseTracking = 'instore_purchase_tracking',
  InstoreEnrollment = 'instore_enrollment',
  OrderingErrorMessages = 'ordering_error_messages',
  CheckoutMessage = 'checkout_message',
  UnavailableItemVisibility = 'unavailable_item_visibility',
  TimeBasedMenuVisibility = 'time_based_menu_visibility',
  PromoCodeVisibility = 'promo_code_visibility',
  TermsAndPolicies = 'terms_and_policies',
  Analytics = 'analytics',
}

const t = buildTranslate('cms')

/** Renders the tabbed settings page within the CMS section, conditionally including tabs based on feature flags and permissions. */
const Settings = () => {
  const deliveryOptimizationEnabled = useFlag('delivery-optimization-mx', true)
  const preferencesModalSettingEnabled = useFlag(
    'preferences-modal-setting-enabled',
    false
  )
  const merchant = useSelector(selectCurrentMerchant)
  const isAdmin = useIsAdmin()
  const customizableLegalDocsFlag = useFlag('mx-customizable-legal-docs', false)
  const isCustomizableRoutesEnabled = customizableLegalDocsFlag && isAdmin
  const instoreEnrollmentEnabled = useFlag('instore-enrollment-settings', false)
  const unavailableItemVisibilityEnabled = useFlag(
    'mx-unavailable-item-visibility',
    false
  )
  const timeBasedMenuVisibilityFlag = useFlag(
    'olo-time-based-menu-visibility-mx',
    false
  )
  const timeBasedMenuVisibilityEnabled =
    timeBasedMenuVisibilityFlag &&
    Boolean(merchant?.supports_time_based_menu_visibility)
  const promoCodeVisibilityEnabled = useFlag(
    'promo-codes-visibility-override',
    false
  )
  const ability = useAbility()
  const orderingErrorMessagesEnabled = useFlag(
    'ordering-error-message-customization',
    false
  )
  const orderingManagementEnabled = useFlag('mx-4-ordering-management', false)
  const { orderingEnabled } = useOrderingAccess()
  const showOrderingErrorMessages =
    orderingErrorMessagesEnabled && orderingManagementEnabled && orderingEnabled

  const orderingConfigurationTab = {
    key: Tabs.OrderingConfiguration,
    component: <OrderingConfiguration />,
    title: t('settings.ordering_configuration.title'),
  }

  const unavailableItemVisibilityTab = {
    key: Tabs.UnavailableItemVisibility,
    component: <UnavailableItemVisibility />,
    title: t('settings.unavailable_item_visibility.title'),
  }

  const timeBasedMenuVisibilityTab = {
    key: Tabs.TimeBasedMenuVisibility,
    component: <TimeBasedMenuVisibility />,
    title: t('settings.time_based_menu_visibility.title'),
  }

  const promoCodeVisibilityTab = {
    key: Tabs.PromoCodeVisibility,
    component: <PromoCodeVisibility />,
    title: t('settings.promo_code_visibility.title'),
  }

  const deliveryDefaultLogicTab = {
    key: Tabs.DeliveryDefaultLogic,
    component: <DeliveryDefaultLogic />,
    title: t('settings.delivery_default_logic.title'),
  }

  const preferencesModalSettingTab = {
    key: Tabs.PreferencesModalSetting,
    component: <PreferencesModalSetting />,
    title: t('settings.preferences_modal_setting.title'),
  }

  const receiptSettingsTab = {
    key: Tabs.ReceiptSettings,
    component: <ReceiptSettings />,
    title: t('settings.receipt_settings.title'),
  }

  const orderingErrorMessagesTab = {
    key: Tabs.OrderingErrorMessages,
    component: <ErrorMessages />,
    title: t('settings.ordering_error_messages.title'),
  }

  const checkoutMessageTab = {
    key: Tabs.CheckoutMessage,
    component: <CheckoutMessage />,
    title: t('settings.checkout_message.title'),
  }

  const scannableCodesTab = {
    key: Tabs.ScannableCodes,
    component: <ScannableCodes />,
    title: t('settings.scannable_codes.title'),
  }

  const postOrderFeedbackTab = {
    key: Tabs.PostOrderFeedback,
    component: <PostOrderFeedback />,
    title: t('settings.post_order_feedback.title'),
  }

  const instorePurchaseTrackingTab = {
    key: Tabs.InstorePurchaseTracking,
    component: <InstorePurchaseTracking />,
    title: t('settings.instore_purchase_tracking.title'),
  }

  const instoreEnrollmentTab = {
    key: Tabs.InstoreEnrollment,
    component: <InstoreEnrollment />,
    title: t('settings.instore_enrollment.title'),
  }

  const termsAndPoliciesTab = {
    key: Tabs.TermsAndPolicies,
    component: <TermsAndPolicies />,
    title: t('settings.terms_and_policies.title'),
  }

  const analyticsTab = {
    key: Tabs.Analytics,
    component: <Analytics />,
    title: t('settings.analytics.tab'),
  }

  const tabs = [
    orderingConfigurationTab,
    ...(unavailableItemVisibilityEnabled ? [unavailableItemVisibilityTab] : []),
    ...(timeBasedMenuVisibilityEnabled ? [timeBasedMenuVisibilityTab] : []),
    ...(promoCodeVisibilityEnabled ? [promoCodeVisibilityTab] : []),
    ...(deliveryOptimizationEnabled &&
    ability.can('manage', 'OrderingManagement')
      ? [deliveryDefaultLogicTab]
      : []),
    ...(preferencesModalSettingEnabled &&
    ability.can('manage', 'OrderingManagement')
      ? [preferencesModalSettingTab]
      : []),
    receiptSettingsTab,
    ...(showOrderingErrorMessages ? [orderingErrorMessagesTab] : []),
    ...(merchant?.ordering_enabled
      ? [
          {
            ...checkoutMessageTab,
            disabled: !ability.can('access', 'OrderingManagement'),
          },
        ]
      : []),
    scannableCodesTab,
    ...(isPostOrderFeedbackHidden(merchant?.ordering_provider)
      ? []
      : [postOrderFeedbackTab]),
    ...(instoreEnrollmentEnabled && merchant?.has_direct_integration_provider
      ? [instoreEnrollmentTab]
      : []),
    instorePurchaseTrackingTab,
    ...(isCustomizableRoutesEnabled ? [termsAndPoliciesTab] : []),
    analyticsTab,
  ]

  return <InnerTabs tabs={tabs} />
}

export default Settings
